import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { Loading } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useGetWhatsappConversationsQuery } from '@/services/endpoints/whatsappInbox';
import { LIST_POLLING_MS } from '../inboxUtils';
import { ConversationList } from './ConversationList';
import { ConversationThread } from './ConversationThread';
import { InboxEmptyState } from './InboxEmptyState';

type Props = {
  selectedId?: string;
  onSelect: (conversationId: string) => void;
  /** Clears selection (used below md to return from thread to list). */
  onBack?: VoidFunction;
  /** When true, skip the conversations query (e.g. connector not ready). */
  skip?: boolean;
};

/** Viewport-bounded shell so list and thread scroll inside the panes, not the page body. */
const INBOX_SHELL =
  'flex h-[calc(100dvh-16rem)] max-h-[calc(100dvh-16rem)] min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-primary-border bg-white-100 md:flex-row';

/** Shared two-pane inbox: conversation list + live thread/empty state. Hosts only supply selection navigation. */
export const WhatsappInboxPanes: FC<Props> = ({ selectedId, onSelect, onBack, skip = false }) => {
  const { t } = useTranslation('whatsappInbox');
  const workspace = useWorkspace();
  const {
    data,
    isLoading: isListLoading,
    isError,
  } = useGetWhatsappConversationsQuery({ workspaceId: workspace.id }, { skip, pollingInterval: LIST_POLLING_MS });
  const conversations = data ?? [];
  const selected = conversations.find((conversation) => conversation.id === selectedId);
  /** Hide the list below md only once a matching conversation is resolved (not merely selectedId). */
  const hasResolvedThread = !!selected;

  return (
    <div className={INBOX_SHELL}>
      <div
        className={twMerge(
          'flex h-full min-h-0 min-w-0 flex-col overflow-hidden md:min-w-[18rem] md:w-[30%] md:max-w-[24rem] md:flex-none',
          hasResolvedThread ? 'hidden md:flex' : 'flex flex-1'
        )}
      >
        {isListLoading ? (
          <Loading />
        ) : isError && !data ? (
          <p className='p-4 text-sm text-primary-900/60'>{t('loadFailed')}</p>
        ) : (
          <ConversationList conversations={conversations} selectedId={selectedId} onSelect={onSelect} />
        )}
      </div>
      {selected ? (
        <div className={twMerge('flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden', hasResolvedThread ? 'flex' : 'hidden md:flex')}>
          <ConversationThread key={selected.id} conversation={selected} onBack={onBack} />
        </div>
      ) : (
        <InboxEmptyState className='hidden h-full min-h-0 min-w-0 flex-1 md:flex' />
      )}
    </div>
  );
};
